import type { App } from '@grafikr/components';
import announcementBar from 'layout/announcement-bar';
import header from 'layout/header';
import navigation from 'layout/navigation';
import navigationMegamenu from 'layout/navigation-megamenu';

import pagination from 'components/pagination';
import sticky from 'components/sticky';

import hasUrlParam from 'utils/loaders/has-url-param';
import intersectionLoader from 'utils/loaders/intersection';
import childIntersectionLoader from 'utils/loaders/on-child-intersection';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onEvent from 'utils/loaders/on-event';
import onInteraction from 'utils/loaders/on-interaction';

export default (app: App) => {
  app.add({
    // Layout
    'announcement-bar': announcementBar,
    header,
    navigation,
    'navigation-megamenu': navigationMegamenu,
    'navigation-drawer': [
      onElementInteraction,
      () =>
        import(/* webpackChunkName: "component-navigation-drawer" */ 'layout/navigation-drawer'),
    ],
    'navigation-subdrawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-navigation-subdrawer" */ 'layout/navigation-subdrawer'
        ),
    ],

    sticky,
    pagination,
    'cart-drawer': [
      [onEvent, onElementInteraction, hasUrlParam],
      () =>
        import(/* webpackChunkName: "component-cart-drawer" */ 'components/reactive/cart-drawer'),
    ],
    'swap-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-swap-content" */ 'components/swap-content'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    'read-more': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-read-more" */ 'components/read-more'),
    ],
    'product-list': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-list" */ 'components/product-list'),
    ],
    'filter-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-filter-drawer" */ 'components/filter-drawer'),
    ],
    filter: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-filter" */ 'components/filter'),
    ],
    'search-suggestions': [
      [onEvent, onElementInteraction],
      () =>
        import(
          /* webpackChunkName: "component-search-suggestions" */ 'components/search-suggestions'
        ),
    ],
    'price-slider': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-price-slider" */ 'components/price-slider'),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'product-item-media': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-item-media" */ 'components/product-item-media'
        ),
    ],
    draggable: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-draggable" */ 'components/draggable'),
    ],
    tab: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-tab" */ 'components/tab'),
    ],
    'product-linked-products': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-linked-products" */ 'components/linked-products'
        ),
    ],
    'product-item': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-item" */ 'components/product-item'),
    ],
    'product-item-look': [
      [intersectionLoader, childIntersectionLoader],
      () =>
        import(
          /* webpackChunkName: "component-product-item-look" */ 'components/product-item-look'
        ),
    ],
    script: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-script" */ 'components/script'),
    ],
    'anchor-links': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-anchor-links" */ 'components/anchor-links'),
    ],
    'quick-shop-drawer': [
      onEvent,
      () =>
        import(
          /* webpackChunkName: "component-quick-shop-drawer" */ 'components/quick-shop-drawer'
        ),
    ],
    'estimated-delivery': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-estimated-delivery" */ 'components/estimated-delivery'
        ),
    ],
    'cart-count': [
      onEvent,
      () => import(/* webpackChunkName: "component-cart-count" */ 'components/cart-count'),
    ],
    'country-selector': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-country-selector" */ 'components/country-selector'),
    ],

    // Pages
    product: [
      [intersectionLoader, childIntersectionLoader],
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-recommendations': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "product-recommendations" */ 'pages/product/recommendations'),
    ],
    'product-sizeguide-drawer': [
      onInteraction,
      () => import(/* webpackChunkName: "product-sizeguide-drawer" */ 'pages/product/sizeguide'),
    ],
    'product-shop-the-look-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "product-shop-the-look-form" */ 'pages/product/shop-the-look-form'
        ),
    ],
    'product-shop-the-look-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "product-shop-the-look-drawer" */ 'pages/product/shop-the-look'
        ),
    ],
    'product-back-in-stock-popover': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "product-back-in-stock-popover" */ 'pages/product/back-in-stock/popover'
        ),
    ],
    'product-back-in-stock-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "product-back-in-stock-form" */ 'pages/product/back-in-stock/form'
        ),
    ],

    // Search
    'search-page': [
      intersectionLoader,
      () => import(/* webpackChunkName: "search-page" */ 'components/reactive/search-page'),
    ],
    // Collections / Filter
    'algolia-collection': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "algolia-collection" */ 'components/reactive/collection-page'),
    ],
    // Algolia Instant Search
    'algolia-instant-search': [
      intersectionLoader,
      () => import(/* webpackChunkName: "instant-search" */ 'components/reactive/instant-search'),
    ],
    'algolia-search-suggestions': [
      [onEvent, onElementInteraction],
      () =>
        import(
          /* webpackChunkName: "component-search-suggestions" */ 'components/reactive/search-suggestions'
        ),
    ],
  });
};
